// @flow
import React, { useState } from 'react';
import { Button, message, Spin, Form, Row, Col } from 'antd';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { request } from 'actions/ajax';
import URL from 'apis/urls';
import classNames from 'classnames';
import { getInitialRange } from 'constants/dateRange';
import { getFileNameFromContentDisposition } from 'utils/downloadHelper';
import layoutStyles from '../layout/Layout.styl';
import styles from './Usage.styl';
import DateRange from '../shared/DateRange';
import SmarterSelect from '../shared/SmarterFormItems/Select';
import PDFViewer from '../shared/PDFViewer';

type Props = {
	intl: {
		formatMessage: (params: { id: string }) => string,
	},
	dispatch: (action: any) => any,
	form: {
		validateFields: (callback: (err: ?Error, values: any) => void) => void,
		getFieldDecorator: (name: string, options?: Object) => (node: React$Node) => React$Node,
	},
};

const Usage = ({ intl, dispatch, form }: Props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [pdfData, setPdfData] = useState<Blob | null>(null);
	const [fileName, setFileName] = useState<string>('');

	const generateReport = (event: SyntheticEvent<HTMLFormElement>) => {
		event.preventDefault();
		form.validateFields((err, values) => {
			if (err) return;

			setLoading(true);
			dispatch(
				request({
					method: 'POST',
					url: URL.USAGE_EXPORT,
					params: {
						from: values.dates.from.unix(),
						to: values.dates.to.unix(),
						productFamily: values.product,
					},
					returnResponseHeaders: true,
					injectProduct: false,
					options: {
						responseType: 'blob',
					},
				})
			)
				.then(async response => {
					try {
						const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
						setPdfData(pdfBlob);
						setFileName(getFileNameFromContentDisposition(response.headers['content-disposition']));
						message.success(intl.formatMessage({ id: 'App.ReportGeneratedSuccess' }));
					} catch (error) {
						throw new Error('Failed to verify PDF');
					}
				})
				.catch(error => {
					message.error(intl.formatMessage({ id: 'App.ReportGenerationFailed' }));
					console.error('PDF Error:', error);
				})
				.finally(() => {
					setLoading(false);
				});
		});
	};

	return (
		<div className={classNames(layoutStyles.container, styles.usage_container)}>
			<h2 className="label-statistics">
				<FormattedMessage id="App.NavAnalyticsUsage" />
			</h2>
			<Spin spinning={loading}>
				<Form onSubmit={generateReport}>
					<Row gutter={[16, 16]}>
						<Col span={12}>
							<Form.Item label={<FormattedMessage id="App.Product.Family" />}>
								{form.getFieldDecorator('product', {
									initialValue: undefined,
								})(
									<SmarterSelect
										className={styles.select}
										globalRegister="availproductsappfullUsageAnalytics"
										disabled={loading}
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item label={<FormattedMessage id="App.DashboardDateRange" />}>
								{form.getFieldDecorator('dates', {
									initialValue: getInitialRange(),
									rules: [
										{ required: true, message: intl.formatMessage({ id: 'App.SelectDateRange' }) },
									],
								})(<DateRange />)}
							</Form.Item>
						</Col>
					</Row>
					<Row>
						<Col>
							<Button
								type="primary"
								htmlType="submit"
								loading={loading}
								className={styles.generate_button}
							>
								<FormattedMessage id="App.GenerateReport" />
							</Button>
						</Col>
					</Row>
				</Form>
			</Spin>
			{pdfData && !loading && (
				<PDFViewer
					file={pdfData}
					fileName={fileName}
					onError={() => {
						setPdfData(null);
						message.error(intl.formatMessage({ id: 'App.PDFLoadError' }));
					}}
				/>
			)}
		</div>
	);
};

const EnhancedUsage = compose(
	injectIntl,
	connect(null, dispatch => ({ dispatch })),
	Form.create()
)(Usage);

export default EnhancedUsage;
